import React from 'react';
import { Box, Button, Card, CardContent, Typography, Alert, CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setStoredAuthMethod } from '../utils/authMethod';
import { useAuth } from '../contexts/AuthContext';

const SamlSessionComplete: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeSsoLogin } = useAuth();
  const code = searchParams.get('code') || '';

  const [status, setStatus] = React.useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    const run = async () => {
      if (!code) {
        setStatus('err');
        setMessage('Missing session code. Start SSO again from the login page.');
        return;
      }
      setStatus('loading');
      setMessage('');
      try {
        const resp = await fetch(`/api/v1/auth/saml/session?code=${encodeURIComponent(code)}`, {
          credentials: 'include'
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || !json?.success || !json?.data?.token) {
          throw new Error(json?.error || `HTTP ${resp.status}`);
        }
        completeSsoLogin(json.data.token, json.data.user, 'saml');
        setStatus('ok');
        navigate('/dashboard', { replace: true });
      } catch (e: unknown) {
        setStatus('err');
        setMessage(e instanceof Error ? e.message : 'Could not complete SAML login');
      }
    };
    run();
  }, [code, navigate]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fc', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
      <Card sx={{ width: '100%', maxWidth: 560, borderRadius: 3, boxShadow: '0 12px 30px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, mb: 2, color: '#111827' }}>
            Completing sign-in…
          </Typography>
          {status === 'loading' && (
            <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={18} sx={{ mr: 1 }} /> Securing your session…
            </Alert>
          )}
          {status === 'err' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={() => navigate('/login')} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Back to Login
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SamlSessionComplete;
